import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { GoFormzAgentContextTypes, GoFormzAgentTemplateContext } from './goFormz-agent-types';
import { applyGoFormzAgentContext } from './actions';
import { useActions } from '../hooks/useActions';
import { DockPosition, DockMode } from './GoFormzAgentPanel';
import { startRecording } from '../helpers/ScreenRecordingUtility';

// Define the shape of the context
interface GoFormzAgentContextType {
  isAgentOpen: boolean;
  selectedFormula: string;
  templateContext: GoFormzAgentTemplateContext;
  dockPosition: DockPosition;
  dockMode: DockMode;
  panelSize: { width: number; height: number };
  openAgent: () => void;
  closeAgent: () => void;
  selectFormula: (formula: string) => void;
  setTemplateContext: (context: GoFormzAgentTemplateContext) => Promise<number>;
  setDockPosition: (position: DockPosition) => void;
  setDockMode: (mode: DockMode) => void;
  setPanelSize: (size: { width: number; height: number }) => void;
}

// Default template context values
const DEFAULT_TEMPLATE_CONTEXT: GoFormzAgentTemplateContext = {
  templateId: '',
  selectedFieldIds: [],
  selectedProperty: '',
  selectedCalculation: '',
  suggestedCalculationSelected: ''
};

// Create the context with a default undefined value
const GoFormzAgentContext = createContext<GoFormzAgentContextType | undefined>(undefined);

// Provider props interface
interface GoFormzAgentProviderProps {
  children: ReactNode;
}

export function GoFormzAgentProvider({ children }: GoFormzAgentProviderProps) {
  // State management
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState('');
  const [templateContext, setTemplateContextState] = useState<GoFormzAgentTemplateContext>(DEFAULT_TEMPLATE_CONTEXT);

  // Panel state
  const [dockPosition, setDockPosition] = useState<DockPosition>(() => {
    const saved = localStorage.getItem('AI_DOCK_POSITION');
    return (saved as DockPosition) || 'right';
  });
  const [dockMode, setDockMode] = useState<DockMode>(() => {
    const saved = localStorage.getItem('AI_DOCK_MODE');
    return (saved as DockMode) || 'inline';
  });
  const [panelSize, setPanelSizeState] = useState({ width: 450, height: 400 });

  // Actions
  const setGoFormzAgentContextAction = useActions(applyGoFormzAgentContext);

  // Methods
  const openAgent = useCallback(() => {
    //start mixpanel recording
    startRecording();
    setIsAgentOpen(true);
  }, []);

  const closeAgent = useCallback(() => {
    setIsAgentOpen(false);
  }, []);

  const selectFormula = useCallback(
    (formula: string) => {
      setSelectedFormula(formula);

      // Also update the template context
      const updatedContext = {
        ...templateContext,
        suggestedCalculationSelected: formula
      };

      setTemplateContextState(updatedContext);
      setGoFormzAgentContextAction(GoFormzAgentContextTypes.Template, updatedContext);
    },
    [templateContext, setGoFormzAgentContextAction]
  );

  const setTemplateContext = useCallback(
    (context: GoFormzAgentTemplateContext): Promise<number> => {
      setTemplateContextState((prevContext) => {
        const newContext = {
          ...prevContext,
          ...context
        };

        return newContext;
      });
      setGoFormzAgentContextAction(GoFormzAgentContextTypes.Template, context);
      return Promise.resolve(1);
    },
    [setGoFormzAgentContextAction]
  );

  const updateDockPosition = useCallback((position: DockPosition) => {
    setDockPosition(position);
    localStorage.setItem('AI_DOCK_POSITION', position);
  }, []);

  const updateDockMode = useCallback((mode: DockMode) => {
    setDockMode(mode);
    localStorage.setItem('AI_DOCK_MODE', mode);
  }, []);

  const updatePanelSize = useCallback((size: { width: number; height: number }) => {
    setPanelSizeState(size);
  }, []);

  // Value object to be provided by the context
  const value = useMemo(
    () => ({
      isAgentOpen,
      selectedFormula,
      templateContext,
      dockPosition,
      dockMode,
      panelSize,
      openAgent,
      closeAgent,
      selectFormula,
      setTemplateContext,
      setDockPosition: updateDockPosition,
      setDockMode: updateDockMode,
      setPanelSize: updatePanelSize
    }),
    [
      isAgentOpen,
      selectedFormula,
      templateContext,
      dockPosition,
      dockMode,
      panelSize,
      openAgent,
      closeAgent,
      selectFormula,
      setTemplateContext,
      updateDockPosition,
      updateDockMode,
      updatePanelSize
    ]
  );

  return <GoFormzAgentContext.Provider value={value}>{children}</GoFormzAgentContext.Provider>;
}

// Custom hook to use the GoFormz Agent context
export function useGoFormzAgentContext() {
  const context = useContext(GoFormzAgentContext);
  if (context === undefined) {
    throw new Error('useGoFormzAgentContext must be used within a GoFormzAgentProvider');
  }
  return context;
}
